import { cn } from "utils/cn";

// A .tsx on purpose: Tailwind reads only `src/**/*.tsx` (tailwind.config.js), and a class written only here — the dark
// variants — would otherwise never be generated.
/**
 * The composer's own surface, for the controls inside it (chips, the idle send button): OpenArt's sleek
 * finish — a hairline edge, a 1px light along the top, a ground that deepens a touch toward the foot. No blur.
 */
export const SLEEK_CONTROL = "sleek-control"; // index.css — the app's one button finish

/** The composer's own box (and the plan card, drawn as it): a hairline edge, a 1px light along its top, a ground that deepens toward the foot. */
export const SLEEK_SURFACE =
  "border border-black-300 bg-body bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.012)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] dark:bg-body-100 dark:bg-[linear-gradient(180deg,rgba(214,225,255,0.03)_0%,rgba(214,225,255,0.06)_100%)] dark:shadow-[inset_0_1px_0_rgba(211,237,248,0.11)]";

/**
 * A button laid over a photograph (the trend wall's actions, a chat result's "Add to chat"): the sleek finish on a solid
 * ground underneath (dark mode's gradient is near-transparent) and a drop shadow to lift it off the picture.
 */
export const SLEEK_ACTION = cn(
  SLEEK_CONTROL,
  "dark:bg-body-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_14px_-6px_rgba(0,0,0,0.35)] dark:shadow-[inset_0_1px_0_rgba(211,237,248,0.08),0_4px_14px_-6px_rgba(0,0,0,0.7)]"
);

/** The page header's buttons (Academy, Ask Agent, What's New, the bell): the control's finish on the page's own ground,
 * so they read over whatever runs under the header (the home's photographs). */
export const SLEEK_TOPBAR = cn(SLEEK_CONTROL, "bg-body dark:bg-body-100");

/** A small menu or panel over the chat (the composer's "+" menu, the approvals, the spend, the context): one compact surface. */
export const SLEEK_MENU = "rounded-xl border border-border bg-popover p-1.5 font-satoshi text-foreground shadow-[0_8px_24px_rgba(0,0,0,0.18)]";

/** A control that holds a choice (a picked moodboard, a brand kit): the same finish, tinted the accent violet. */
export const SLEEK_ACCENT =
  "border border-purple/15 bg-[linear-gradient(180deg,rgb(98_102_240/0.11)_0%,rgb(98_102_240/0.16)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:border-purple/20 dark:bg-[linear-gradient(180deg,rgb(98_102_240/0.18)_0%,rgb(98_102_240/0.24)_100%)] dark:shadow-[inset_0_1px_0_rgba(211,237,248,0.08)]";

/** The send button when it can send: the same finish on ink — a dark ground lit along its top edge (light in dark). */
export const SLEEK_PRIMARY = "sleek-ink"; // index.css — the app's one button finish, on ink

/**
 * The agent's accent on words, standing still: the waiting label's colour (`AgentWaitingText`: the primary violet, lime in
 * dark, a band through its middle — darker on light ground, lighter on dark, so it always reads as a sheen) without its moving light — for a title that is the agent's own, not its work.
 */
export const AGENT_ACCENT_TEXT =
  "bg-[linear-gradient(90deg,rgb(var(--th-purple-green))_0%,color-mix(in_srgb,rgb(var(--th-purple-green))_55%,black)_50%,rgb(var(--th-purple-green))_100%)] dark:bg-[linear-gradient(90deg,rgb(var(--th-purple-green))_0%,color-mix(in_srgb,rgb(var(--th-purple-green))_45%,white)_50%,rgb(var(--th-purple-green))_100%)] bg-clip-text text-transparent [-webkit-text-fill-color:transparent]";

/**
 * The chat's compact card (the trend search, an ended plan's record): the frame (the design's reference card): a hairline edge with a halo — a faint light glowing
 * inward from it — around a ground of its own. Measured off the reference screenshot (2026-10-19): 24 px corner (the reference had 16), the edge
 * ~white 15 %, the glow fading over ~7 px.
 */
export const AGENT_CARD =
  "rounded-[24px] border-[0.5px] border-black-300 bg-body shadow-[inset_0_0_8px_rgba(0,0,0,0.05)] dark:border-white/[0.16] dark:bg-[#121314] dark:shadow-[inset_0_0_8px_rgba(255,255,255,0.08)]";
